import { APP_LOCATION_EVENT, readPage, readLocation, locationIndex, pageHref, writeLocation } from "./utils/appLocation";
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { apiFetch } from "./api";
import { requestAppNavigation } from './hooks/useUnsavedChanges';
import {
  ATIVIDADE_RECENTE_PARA_RENOVACAO,
  css,
  INTERVALO_RENOVACAO_SESSAO,
  TEMPO_INATIVIDADE,
} from "./constants";
import { Spinner, Icon, Modal, Btn } from "./components";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { TenantContext, hasFeature } from "./context/TenantContext";
import { getIntegracaoNome } from "./utils/tenantLabels";
import {
  clearAuthenticatedObservability,
  configureAuthenticatedObservability,
  recordPageNavigation,
  recordUiError,
  reportOperationalProblem,
  setCurrentOperationalPage,
} from "./observability";

import Brand from "./components/Brand";

import LoginPage from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import ChangePasswordModal from "./components/ChangePasswordModal";
import { getResetToken, clearResetToken } from "./resetLink";
import Dashboard from "./pages/Dashboard";
import FuncionariosPage from "./pages/Funcionarios";
import MotoristasPage from "./pages/Motoristas";
import VeiculosPage from "./pages/Veiculos";
import DespesasFrota from "./pages/DespesasFrota";
import BuscarRotasEmbraerPage from "./pages/BuscarRotasEmbraer";
import DocumentosPage from "./pages/Documentos";
import RelatoriosPage from "./pages/Relatorios";
import LogsPage from "./pages/Logs";
import UsuariosPage from "./pages/Usuarios";
import ClientesRotaflexPage from "./pages/ClientesRotaflex";
import EmpresasDestinoPage from "./pages/EmpresasDestino";
import DisparoManualPage from "./pages/DisparoManual";
import DisparoManualLegacyPage from "./pages/DisparoManualLegacy";
import NotificacoesPage from "./pages/Notificacoes";
import TenantBillingPage from "./pages/TenantBilling";
import PlanosPage from "./pages/Planos";
import PedidosPage from "./pages/Pedidos";

const ROUTE_DRAFT_EDIT_STORAGE_KEY = "rotaflex_route_draft_edit_id";
const LEGACY_PEDIDOS_PAGES = new Set(["programacoes", "rota", "backlog-rotas"]);

const normalizePageId = (pageId) => (
  LEGACY_PEDIDOS_PAGES.has(pageId) ? "pedidos" : pageId
);

const PAGE_FEATURE_MAP = {
  pedidos: "routes",
  programacoes: "routes",
  rota: "routes",
  "backlog-rotas": "routes",
  "buscar-embraer": "automations",
  documentos: "documents",
  relatorios: "reports",
};

function Sidebar({ page, setPage, onLogout, onReportProblem, onPasswordChange, userEmail, userRole, isMobile, isOpen, onClose, tenantFeatures, tenantConfig, operationalFlags }) {
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const isSuperadmin = userRole === "superadmin";
  const integracao = getIntegracaoNome(tenantConfig) || "Embraer";
  const [fleetAlerts, setFleetAlerts] = useState(null);
  const navigationRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!isMobile || !isOpen) return undefined;
    const previousFocus = document.activeElement;
    navigationRef.current?.querySelector('[aria-current="page"]')?.focus();
    const handleKey = event => {
      if (document.querySelector('[role="dialog"]')) return;
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); }
      if (event.key === 'Tab') {
        const buttons = Array.from(navigationRef.current?.querySelectorAll('a[href],button:not([disabled])') || []);
        const first = buttons[0];
        const last = buttons.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [isMobile, isOpen]);
  useEffect(() => {
    let active = true;
    const update = () => apiFetch('/frota/lembretes/resumo')
      .then(data => { if (active) setFleetAlerts(data); })
      .catch(() => { if (active) setFleetAlerts(null); });
    update();
    const interval = setInterval(update, 60000);
    window.addEventListener('frota-atualizada', update);
    return () => { active = false; clearInterval(interval); window.removeEventListener('frota-atualizada', update); };
  }, []);

  const navVisible = (id) => {
    if (id === "buscar-embraer" && !isSuperadmin && tenantConfig?.embraer_sgt_habilitado !== true) return false;
    if (isSuperadmin && !Object.keys(tenantFeatures || {}).length) return true;
    const feature = PAGE_FEATURE_MAP[id];
    if (!feature) return true;
    return hasFeature(tenantFeatures, feature);
  };

  const NAV_GROUPS = [
    {
      label: "Operação",
      items: [
        { id: "dashboard",    label: "Dashboard",        icon: "dashboard" },
        { id: "pedidos",      label: "Pedidos",          icon: "orders" },
        { id: "buscar-embraer", label: `Importar pedidos ${integracao}`, icon: "import" },
        { id: "notificacoes", label: "Notificações", icon: "bell" },
        { id: "despesas-frota", label: "Despesas da frota", icon: "receipt" },
        ...(!isSuperadmin && operationalFlags?.contingency_manual_dispatch_enabled
          ? [{ id: "disparo-manual", label: "Central de Contingência", icon: "warning" }]
          : !isSuperadmin && operationalFlags?.legacy_manual_dispatch_enabled
            ? [{ id: "disparo-manual", label: "Disparo Manual", icon: "warning" }]
            : []),
        { id: "documentos",   label: "Documentos",       icon: "document" },
        { id: "relatorios",   label: "Relatórios",       icon: "report" },
        { id: "billing",      label: "Plano e uso",      icon: "card" },
      ].filter((item) => navVisible(item.id) && (item.id !== "billing" || !isSuperadmin)),
    },
    {
      label: "Cadastros",
      items: [
        { id: "pessoas",         label: "Funcionários",    icon: "people" },
        { id: "motoristas",      label: "Motoristas",      icon: "driver" },
        { id: "veiculos",        label: "Veículos",        icon: "fleet" },
        { id: "empresas-destino",label: "Empresas de destino",icon: "building" },
      ].filter(item => item.id !== "empresas-destino" || isAdmin),
    },
    ...(isAdmin ? [{
      label: "Administração",
      items: [
        { id: "logs",     label: "Histórico de alterações",              icon: "history" },
        { id: "usuarios", label: "Usuários",          icon: "shield" },
        ...(isSuperadmin ? [{ id: "clientes", label: "Clientes RotaFlex", icon: "building" }] : []),
        ...(isSuperadmin ? [{ id: "planos", label: "Planos", icon: "layers" }] : []),
      ],
    }] : []),
  ];

  const handleNav = (id) => {
    setPage(id);
    if (isMobile) onClose();
  };

  return (
    <div ref={navigationRef} id="rotaflex-navigation" aria-label="Menu principal" aria-hidden={isMobile && !isOpen ? true : undefined} inert={isMobile && !isOpen ? "" : undefined} style={{
      width: 256,
      background: "var(--sidebar)",
      borderRight: "1px solid var(--border-subtle)",
      display: "flex",
      flexDirection: "column",
      height: "100dvh",
      position: "fixed",
      left: 0,
      top: 0,
      zIndex: 99,
      // Animação de slide: no mobile, esconde para a esquerda quando fechado
      transform: isMobile && !isOpen ? "translateX(-256px)" : "translateX(0)",
      transition: "transform 0.25s ease",
      visibility: isMobile && !isOpen ? "hidden" : "visible",
    }}>
      <div style={{ flexShrink: 0, padding: "24px 20px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Brand compact />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="rf-sidebar-wordmark">Rota<span>Flex</span></div>
            <div title={tenantConfig?.display_name || undefined} style={{ fontSize: '0.875rem', color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{isSuperadmin ? "Administração global" : tenantConfig?.display_name || "Gestão de transporte"}</div>
          </div>
          {/* Botão de fechar visível apenas no mobile */}
          {isMobile && (
            <button
              onClick={onClose}
              aria-label="Fechar menu"
              style={{ minHeight: 44, minWidth: 44, background: "none", border: "none", color: "var(--muted)", fontSize: '1.375rem', cursor: "pointer", lineHeight: 1, padding: "2px 4px", flexShrink: 0 }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <nav className="rf-navigation" aria-label="Navegação principal" style={{ flex: 1, minHeight: 0, padding: "4px 12px", display: "flex", flexDirection: "column", gap: 0, overflowY: "auto" }}>
        {NAV_GROUPS.map((group, gi) => (
          <div key={group.label} style={{ marginTop: gi > 0 ? 18 : 0 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 500, letterSpacing: ".02em", color: "var(--muted)", padding: "6px 10px 8px" }}>
              {group.label}
            </div>
            {group.items.map(n => (
              <a key={n.id} href={pageHref(n.id)} aria-current={page === n.id ? "page" : undefined} onClick={event => {
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault(); handleNav(n.id);
              }} style={{
                minHeight: 48,
                display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6,
                border: "1px solid transparent",
                cursor: "pointer", fontSize: '0.9375rem', fontWeight: page === n.id ? 550 : 400,
                textAlign: "left", width: "100%", transition: "background-color 0.15s, color 0.15s", marginBottom: 2,
              }}>
                <Icon name={n.icon} size={16} /><span className="rf-navigation-label">{n.label}</span>
                {n.id === 'despesas-frota' && fleetAlerts && (fleetAlerts.vencidos > 0 || fleetAlerts.sem_km > 0) && <span title={`${fleetAlerts.vencidos} manutenção(ões) devida(s); ${fleetAlerts.sem_km} lembrete(s) sem KM informado`} aria-label={`${fleetAlerts.vencidos} manutenções devidas; ${fleetAlerts.sem_km} sem KM`} style={{ marginLeft: 'auto', color: '#fca5a5', fontWeight: 700 }}>!</span>}
              </a>
            ))}
          </div>
        ))}
      </nav>

      <div className="rf-sidebar-footer">
        <button className="rf-support-trigger" onClick={onReportProblem}><Icon name="warning" size={16} /> Reportar problema</button>
        <div className="rf-sidebar-email" title={userEmail}>{userEmail}</div>
        <div className="rf-sidebar-tools">
          <button onClick={onPasswordChange} aria-label="Alterar minha senha" title="Alterar minha senha"><Icon name="lock" size={15} /> Minha senha</button>
          <button onClick={onLogout}><Icon name="logout" size={15} /> Sair</button>
        </div>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  onPasswordChange: PropTypes.func,
  tenantConfig: PropTypes.shape({ embraer_sgt_habilitado: PropTypes.bool, display_name: PropTypes.string }),
};

function OperationalSupportModal({ onClose }) {
  const [category, setCategory] = useState("route_dispatch");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setSending(true);
    setResult(null);
    const response = await reportOperationalProblem({ category, description });
    setSending(false);
    if (!response.ok) {
      setResult({ error: "O monitoramento não está habilitado neste ambiente." });
      return;
    }
    setResult({
      ok: `Relato enviado. Referência ${String(response.eventId || "registrada").slice(-8)}.`,
    });
  };

  return (
    <Modal title="Reportar problema operacional" onClose={() => !sending && onClose()} maxWidth={520}>
        <p style={{ color: "var(--muted)", fontSize: '0.9375rem', lineHeight: 1.5, margin: "8px 0 16px" }}>
          Descreva a dificuldade. O diagnóstico inclui informações técnicas para ajudar o suporte. Não informe dados pessoais.
        </p>
        <label style={{ display: "grid", gap: 6, fontSize: '0.9375rem', marginBottom: 12 }}>
          O que aconteceu?
          <select value={category} onChange={(event) => setCategory(event.target.value)} style={{ background: "var(--s1)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 8, padding: "10px 12px" }}>
            <option value="route_dispatch">Não consegui enviar a rota</option>
            <option value="required_data">O sistema pediu/cobrou um dado</option>
            <option value="missing_data">Dados ou pedidos não apareceram</option>
            <option value="screen_stuck">A tela travou ou não avançou</option>
            <option value="other">Outro problema</option>
          </select>
        </label>
        <label style={{ display: "grid", gap: 6, fontSize: '0.9375rem' }}>
          Descrição curta (opcional)
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value.slice(0, 300))}
            rows={3}
            placeholder="Ex.: cliquei em Confirmar e a tela não avançou. Não informe nomes, telefones ou documentos."
            style={{ resize: "vertical", background: "var(--s1)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 8, padding: "10px 12px", font: "inherit" }}
          />
        </label>
        {result?.error && <p role="alert" className="planning-error">{result.error}</p>}
        {result?.ok && <p role="status" className="rf-success-text">{result.ok}</p>}
        <div className="rf-form-actions">
          <Btn variant="secondary" onClick={onClose} disabled={sending}>{result?.ok ? 'Fechar' : 'Cancelar'}</Btn>
          {!result?.ok && <Btn onClick={submit} disabled={sending}>{sending ? 'Enviando…' : 'Enviar diagnóstico'}</Btn>}
        </div>
    </Modal>
  );
}

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function App() {
  // useState é a memória do componente — guarda valores entre re-renderizações
  const [loggedIn, setLoggedIn] = useState(false);
  const [resetToken, setResetToken] = useState(getResetToken);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [sessionNotice, setSessionNotice] = useState("");
  const loggedInRef = useRef(loggedIn);
  loggedInRef.current = loggedIn;
  const [page, setPage] = useState(() => normalizePageId(readPage() || localStorage.getItem("rotaflex_page") || "dashboard"));
  const [documentToOpenId, setDocumentToOpenId] = useState(null);
  const [pedidosNav, setPedidosNav] = useState(null);
  const [supportReportOpen, setSupportReportOpen] = useState(false);

  const navigateTo = (newPage, options = {}) => requestAppNavigation(() => {
    const normalizedPage = normalizePageId(newPage);
    recordPageNavigation(normalizedPage, page);
    if (normalizedPage !== "pedidos") {
      clearStoredDraftRouteEdit();
      setPedidosNav(null);
    }
    if (options.pedidosNav) {
      setPedidosNav(options.pedidosNav);
    } else if (normalizedPage === 'pedidos' && page === 'pedidos') {
      setPedidosNav({ tab: readLocation().searchParams.get('etapa') || 'a_fazer' });
      writeLocation({ montagem: null }, { replace: true });
    }
    writeLocation({ page: normalizedPage, ...(options.pedidosNav ? { etapa: options.pedidosNav.tab, data: options.pedidosNav.focusDate, rota: options.pedidosNav.routeId } : {}) }, { clear: normalizedPage !== page });
    setPage(normalizedPage);
    localStorage.setItem("rotaflex_page", normalizedPage);
  });
  useEffect(() => {
    if (window.history.state?.rotaflexIndex == null) window.history.replaceState({ ...window.history.state, rotaflexIndex: 0 }, '');
    let current = { index: locationIndex(), url: readLocation().href };
    let restoring = false;
    let bypass = false;
    const remember = () => { current = { index: locationIndex(), url: readLocation().href }; };
    const apply = () => {
      setPage(normalizePageId(readPage() || 'dashboard'));
      setPedidosNav(null);
      window.dispatchEvent(new CustomEvent('rotaflex:restore-location'));
      remember();
    };
    const pop = () => {
      if (restoring) { restoring = false; return; }
      if (bypass) { bypass = false; apply(); return; }
      const delta = locationIndex() - current.index;
      let blocked = false;
      const accepted = requestAppNavigation(() => {
        if (blocked && delta) { bypass = true; window.history.go(delta); }
        else apply();
      });
      if (!accepted) {
        blocked = true;
        if (delta) { restoring = true; window.history.go(-delta); }
        else window.history.replaceState(window.history.state, '', current.url);
      }
    };
    window.addEventListener(APP_LOCATION_EVENT, remember);
    window.addEventListener('popstate', pop);
    return () => { window.removeEventListener(APP_LOCATION_EVENT, remember); window.removeEventListener('popstate', pop); };
  }, []);
  const openPedidos = (nav = {}) => {
    navigateTo("pedidos", {
      pedidosNav: {
        tab: nav.tab || "a_fazer",
        routeId: nav.routeId || null,
        planning: nav.planning || null,
        draftRoute: nav.draftRoute || null,
        highlightIds: nav.highlightIds || null,
        focusDate: nav.focusDate || null,
      },
    });
  };
  const consumePedidosNavigation = () => setPedidosNav(null);
  const openDocument = (documentId) => {
    setDocumentToOpenId(Number(documentId) || null);
    navigateTo("documentos");
  };
  const [pessoas, setPessoas] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [referenceState, setReferenceState] = useState("loading");
  const [referenceInitialized, setReferenceInitialized] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [tenantSession, setTenantSession] = useState({
    features: {},
    limits: {},
    usage: null,
    config: {},
    operational_flags: {},
    user_id: null,
    plan_code: null,
    plan_name: null,
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isMobile = useMediaQuery("(max-width: 1023px)");
  const clearStoredDraftRouteEdit = () => localStorage.removeItem(ROUTE_DRAFT_EDIT_STORAGE_KEY);
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const isSuperadmin = userRole === "superadmin";
  const allowedPages = new Set([
    "dashboard",
    "pedidos",
    "pessoas",
    "motoristas",
    "veiculos",
    "despesas-frota",
    ...(isSuperadmin || tenantSession.config?.embraer_sgt_habilitado === true ? ["buscar-embraer"] : []),
    "notificacoes",
    ...(
      !isSuperadmin && (
        tenantSession.operational_flags?.contingency_manual_dispatch_enabled
        || tenantSession.operational_flags?.legacy_manual_dispatch_enabled
      )
        ? ["disparo-manual"]
        : []
    ),
    "documentos",
    "relatorios",
    "billing",
    ...(isAdmin ? ["logs", "usuarios", "empresas-destino"] : []),
    ...(isSuperadmin ? ["clientes", "planos"] : []),
  ]);

  // useRef guarda um valor que não dispara re-renderização — ideal para o timer
  const timerRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());
  const lastReportedActivityRef = useRef(0);
  const idleTimeoutMsRef = useRef(TEMPO_INATIVIDADE);

  const loadReferenceData = async () => {
    setReferenceState('loading');
    const results = await Promise.allSettled([apiFetch('/funcionarios'), apiFetch('/motoristas'), apiFetch('/veiculos')]);
    const setters = [setPessoas, setMotoristas, setVeiculos];
    results.forEach((result, index) => { if (result.status === 'fulfilled') setters[index](Array.isArray(result.value) ? result.value : []); });
    setReferenceState(results.some(result => result.status === 'rejected') ? 'error' : 'ready');
    setReferenceInitialized(true);
  };

  // Verifica se existe sessão ativa e carrega os dados iniciais
  const checkAuthAndLoad = async () => {
    // Verifica sessão via cookie httpOnly (prod) ou token localStorage (dev/compat)
    try {
      if (localStorage.getItem("rotaflex_logout_pending")) {
        await apiFetch("/auth/logout", { method: "POST" });
        localStorage.removeItem("rotaflex_logout_pending");
      }
      const usuario = await apiFetch("/auth/me");
      const idleSeconds = Number(usuario.session_policy?.idle_timeout_seconds);
      idleTimeoutMsRef.current = Number.isInteger(idleSeconds) && idleSeconds >= 300 && idleSeconds <= 7200
        ? idleSeconds * 1000 : TEMPO_INATIVIDADE;
      setUserEmail(usuario.email || "");
      setUserName(usuario.nome || "");
      setUserRole(usuario.role || "");
      setTenantSession({
        features: usuario.features || {},
        limits: usuario.limits || {},
        usage: usuario.usage || null,
        config: { ...(usuario.config || {}), cliente_id: usuario.cliente_id ?? null },
        geography: usuario.geography || null,
        plan_code: usuario.plan_code || null,
        plan_name: usuario.plan_name || null,
        operational_flags: usuario.operational_flags || {},
        user_id: usuario.id || null,
      });
      setCurrentOperationalPage(page);
      void configureAuthenticatedObservability({
        userId: usuario.id || null,
        tenantId: usuario.cliente_id || null,
        role: usuario.role || null,
      });
      setPage(normalizePageId(readPage() || localStorage.getItem("rotaflex_page") || "dashboard"));
      if (!readPage()) writeLocation({ page: normalizePageId(localStorage.getItem("rotaflex_page") || "dashboard") }, { replace: true });
      setLoggedIn(true);
      setLoaded(true);

      void (async () => {
        try {
          await loadReferenceData();

          const persistedDraftId = localStorage.getItem(ROUTE_DRAFT_EDIT_STORAGE_KEY);
          const persistedPage = normalizePageId(localStorage.getItem("rotaflex_page") || "");
          if (persistedDraftId && persistedPage === "pedidos") {
            try {
              const draft = await apiFetch(`/rotas/${persistedDraftId}`);
              if (draft?.status === "rascunho") {
                setPedidosNav({
                  tab: "operacao",
                  routeId: null,
                  planning: null,
                  draftRoute: draft,
                });
              } else {
                clearStoredDraftRouteEdit();
              }
            } catch {
              clearStoredDraftRouteEdit();
            }
          }
        } catch (e) {
          console.error("Erro ao carregar dados iniciais:", e);
        }
      })();
    } catch {
      // Sem sessão válida — limpa tudo e vai para dashboard no próximo login
      void clearAuthenticatedObservability();
      localStorage.removeItem("rotaflex_token");
      localStorage.removeItem("rotaflex_page");
      setPage("dashboard");
    } finally {
      setLoaded(true);
    }
  };

  const endLocalSession = (message = "Sua sessão expirou. Entre novamente.") => {
    writeLocation({ page: "dashboard" }, { replace: true, clear: true });
    void clearAuthenticatedObservability().catch(() => {});
    localStorage.removeItem("rotaflex_token");
    localStorage.removeItem("rotaflex_page");
    localStorage.removeItem("rotaflex_last_activity_at");
    clearStoredDraftRouteEdit();
    setLoggedIn(false); setLoaded(true); setPage("dashboard");
    setPasswordModalOpen(false); setSessionNotice(message);
    setReferenceInitialized(false);
    setPessoas([]); setMotoristas([]); setVeiculos([]);
    setUserEmail(""); setUserName(""); setUserRole("");
    localStorage.setItem("rotaflex_session_ended", String(Date.now()));
  };
  const requestLogout = async (notice = "Sessão encerrada.") => {
    localStorage.setItem("rotaflex_logout_pending", "1");
    try {
      await apiFetch("/auth/logout", { method: "POST" });
      localStorage.removeItem("rotaflex_logout_pending");
      endLocalSession(notice);
    } catch {
      endLocalSession("A saída ficou pendente por falta de comunicação. Ao entrar novamente, concluiremos o encerramento da sessão anterior.");
    }
  };
  const scheduleIdleCheck = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const lastActivity = Math.max(lastActivityAtRef.current, Number(localStorage.getItem("rotaflex_last_activity_at")) || 0);
    const remaining = idleTimeoutMsRef.current - (Date.now() - lastActivity);
    timerRef.current = setTimeout(() => {
      if (!loggedInRef.current) return;
      const shared = Math.max(lastActivityAtRef.current, Number(localStorage.getItem("rotaflex_last_activity_at")) || 0);
      if (Date.now() - shared < idleTimeoutMsRef.current) return scheduleIdleCheck();
      void requestLogout("Sessão encerrada por inatividade. Entre novamente.");
    }, Math.max(remaining, 1000));
  };
  const resetTimer = () => {
    lastActivityAtRef.current = Date.now();
    if (loggedInRef.current) {
      const previous = Number(localStorage.getItem("rotaflex_last_activity_at")) || 0;
      if (Date.now() - previous > 5000) localStorage.setItem("rotaflex_last_activity_at", String(Date.now()));
    }
    scheduleIdleCheck();
  };

  const sessionHandlersRef = useRef(null);
  sessionHandlersRef.current = { checkAuthAndLoad, resetTimer, endLocalSession, hasResetLink: resetToken !== null };
  useEffect(() => {
    const openResetLink = () => setResetToken(getResetToken());
    window.addEventListener("rotaflex-password-reset-link", openResetLink);
    return () => window.removeEventListener("rotaflex-password-reset-link", openResetLink);
  }, []);
  useEffect(() => {
    if (!sessionHandlersRef.current.hasResetLink) sessionHandlersRef.current.checkAuthAndLoad();
    const resetIdle = () => sessionHandlersRef.current.resetTimer();
    const onExpired = () => sessionHandlersRef.current.endLocalSession();
    const onStorage = (event) => {
      if (event.key === "rotaflex_session_ended" && loggedInRef.current) {
        // Não retransmitir o evento para evitar ciclos entre abas.
        setLoggedIn(false); setPasswordModalOpen(false);
        setSessionNotice("Sessão encerrada em outra aba. Entre novamente.");
      }
    };
    window.addEventListener("rotaflex-session-expired", onExpired);
    window.addEventListener("storage", onStorage);

    // Escuta qualquer movimento/clique/tecla para reiniciar o timer
    window.addEventListener("mousemove", resetIdle);
    window.addEventListener("keydown", resetIdle);
    window.addEventListener("click", resetIdle);
    window.addEventListener("touchstart", resetIdle, { passive: true });
    const handleInvalidField = (event) => {
      recordUiError({
        message: event.target?.validationMessage || "Campo obrigatório ou inválido.",
        code: "browser_form_validation",
        source: "browser_validation",
      });
    };
    document.addEventListener("invalid", handleInvalidField, true);
    resetIdle();

    // Cleanup: remove os listeners quando o componente desmonta
    return () => {
      window.removeEventListener("mousemove", resetIdle);
      window.removeEventListener("keydown", resetIdle);
      window.removeEventListener("click", resetIdle);
      window.removeEventListener("touchstart", resetIdle);
      window.removeEventListener("rotaflex-session-expired", onExpired);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("invalid", handleInvalidField, true);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!loggedIn) return undefined;

    // Uma pessoa pode passar vários minutos preenchendo um formulário sem
    // chamar a API. O heartbeat renova a sessão apenas quando a aba está
    // visível e houve atividade recente; não mantém uma aba abandonada logada.
    const heartbeatId = window.setInterval(async () => {
      const activeRecently = Date.now() - lastActivityAtRef.current
        <= ATIVIDADE_RECENTE_PARA_RENOVACAO;
      if (document.visibilityState !== "visible" || !activeRecently || lastActivityAtRef.current <= lastReportedActivityRef.current) return;

      try {
        const activityAt = lastActivityAtRef.current;
        await apiFetch("/auth/activity", { method: "POST" });
        lastReportedActivityRef.current = activityAt;
      } catch (error) {
        if (error?.status === 401 || error?.status === 403) sessionHandlersRef.current.endLocalSession();
      }
    }, INTERVALO_RENOVACAO_SESSAO);

    return () => window.clearInterval(heartbeatId);
  }, [loggedIn]);

  useEffect(() => {
    if (!loaded || !loggedIn) return;
    if (allowedPages.has(page)) return;
    navigateTo("dashboard");
  }, [loaded, loggedIn, page, isAdmin, isSuperadmin, tenantSession.config?.embraer_sgt_habilitado]);

  const handleLogout = () => requestLogout();

  useEffect(() => {
    const titles = {
      dashboard: "Dashboard", pedidos: "Pedidos", pessoas: "Passageiros",
      motoristas: "Motoristas", veiculos: "Veículos", "despesas-frota": "Despesas da frota",
      "buscar-embraer": "Importar pedidos", documentos: "Documentos", relatorios: "Relatórios",
      notificacoes: "Notificações", usuarios: "Usuários", clientes: "Clientes", planos: "Planos",
      billing: "Plano e uso", logs: "Logs", "empresas-destino": "Empresas de destino",
      "disparo-manual": "Contingência",
    };
    document.title = `${resetToken !== null ? "Redefinir senha" : loggedIn ? titles[page] || "Gestão de rotas" : "Entrar"} | RotaFlex`;
  }, [page, loggedIn, resetToken]);

  if (resetToken !== null) return <><style>{css}</style><ResetPassword key={resetToken} token={resetToken} onReturn={() => {
    clearResetToken(); setResetToken(null); endLocalSession("");
  }} /></>;

  // Exibe spinner enquanto os dados iniciais ainda estão carregando
  if (!loaded) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
      <Spinner />
    </div>
  );

  // Se não logado, exibe a tela de login
  if (!loggedIn) return (
    <>
      <style>{css}</style>
      <LoginPage notice={sessionNotice} onLogin={() => { setSessionNotice(""); setLoaded(false); checkAuthAndLoad(); resetTimer(); }} />
    </>
  );

  // Fichário de telas: pages[page] busca a tela pelo nome e injeta no <main>.
  const pages = {
    dashboard: <Dashboard pessoas={pessoas} motoristas={motoristas} veiculos={veiculos} setPage={navigateTo} openPedidos={openPedidos} userName={userName} />,
    pessoas: <FuncionariosPage pessoas={pessoas} setPessoas={setPessoas} userRole={userRole} />,
    motoristas: <MotoristasPage motoristas={motoristas} setMotoristas={setMotoristas} userRole={userRole} />,
    "despesas-frota": <DespesasFrota attachmentScope={`${tenantSession.user_id}:${tenantSession.config?.cliente_id ?? "global"}`} />,
    veiculos: <VeiculosPage veiculos={veiculos} setVeiculos={setVeiculos} userRole={userRole} />,
    pedidos: (
      <PedidosPage
        pessoas={pessoas}
        motoristas={motoristas}
        veiculos={veiculos}
        userRole={userRole}
        onOpenDocuments={openDocument}
        initialNav={pedidosNav}
        onConsumeNavigation={consumePedidosNavigation}
      />
    ),
    "buscar-embraer": <BuscarRotasEmbraerPage onOpenPedidos={openPedidos} userRole={userRole} />,
    "disparo-manual": tenantSession.operational_flags?.contingency_manual_dispatch_enabled
      ? <DisparoManualPage />
      : <DisparoManualLegacyPage />,
    notificacoes: <NotificacoesPage userRole={userRole} onOpenPedidos={openPedidos} />,
    documentos: (
      <DocumentosPage
        userRole={userRole}
        initialDocumentId={documentToOpenId}
        onInitialDocumentOpened={() => setDocumentToOpenId(null)}
      />
    ),
    relatorios: <RelatoriosPage />,
    billing: <TenantBillingPage />,
    ...(isAdmin ? {
      "empresas-destino": <EmpresasDestinoPage userRole={userRole} />,
      logs: <LogsPage />,
      usuarios: <UsuariosPage userEmail={userEmail} userRole={userRole} setUserName={setUserName} />,
    } : {}),
    ...(isSuperadmin ? {
      clientes: <ClientesRotaflexPage />,
      planos: <PlanosPage />,
    } : {}),
  };
  const currentPage = allowedPages.has(page) ? pages[page] || pages.dashboard : pages.dashboard;

  return (
  <TenantContext.Provider value={tenantSession}>
    <>
      <style>{css}</style>
      <a className="rf-skip" href="#conteudo-principal">Ir para o conteúdo</a>
      <div style={{ display: "flex", minHeight: "100vh" }}>

        {/* Backdrop semitransparente que fecha a sidebar no mobile ao clicar fora */}
        {isMobile && sidebarOpen && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 98 }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar
          page={page}
          setPage={navigateTo}
          onLogout={() => requestAppNavigation(handleLogout)}
          onReportProblem={() => setSupportReportOpen(true)}
          onPasswordChange={() => setPasswordModalOpen(true)}
          userEmail={userEmail}
          userRole={userRole}
          isMobile={isMobile}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          tenantFeatures={tenantSession.features}
          tenantConfig={tenantSession.config}
          operationalFlags={tenantSession.operational_flags}
        />

        {passwordModalOpen && <ChangePasswordModal onClose={() => setPasswordModalOpen(false)} onChanged={() => endLocalSession("Senha alterada. Entre novamente.")} />}

        {supportReportOpen && (
          <OperationalSupportModal onClose={() => setSupportReportOpen(false)} />
        )}

        {/* Top bar fixo no mobile com botão hamburger */}
        {isMobile && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, height: 56,
            background: "var(--s1)", borderBottom: "1px solid var(--border)",
            display: "flex", alignItems: "center", padding: "0 16px", gap: 12,
            zIndex: 50,
          }}>
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Abrir menu" aria-expanded={sidebarOpen} aria-controls="rotaflex-navigation"
              style={{ minHeight: 44, minWidth: 44, background: "none", border: "none", color: "var(--text)", fontSize: '1.375rem', cursor: "pointer", padding: "4px 6px", display: "flex", alignItems: "center", lineHeight: 1 }}
            >
              <Icon name="menu" size={22} />
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Brand />
            </div>
          </div>
        )}

        <main id="conteudo-principal" tabIndex={-1} inert={isMobile && sidebarOpen ? "" : undefined} style={{
          flex: 1,
          minWidth: 0,
          marginLeft: isMobile ? 0 : 256,
          padding: isMobile ? 16 : 32,
          // No mobile, a top bar tem 56px — adiciona padding extra no topo para não sobrepor
          paddingTop: isMobile ? 72 : 32,
          minHeight: "100vh",
          background: "var(--bg)",
        }}>
          <div className="rf-content">
            {referenceState === 'error' && <div className="rf-inline-notice" role="alert"><div><strong>Alguns cadastros não carregaram.</strong><p>Funcionários, motoristas ou veículos podem estar incompletos. Recarregue antes de planejar.</p></div><Btn variant="secondary" onClick={loadReferenceData}>Recarregar cadastros</Btn></div>}
            {referenceState === 'loading' && !referenceInitialized
              ? <div className="rf-loading-state" role="status"><Spinner /> Carregando cadastros da operação…</div> : currentPage}
          </div>
        </main>
      </div>
    </>
  </TenantContext.Provider>
  );
}
